import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Form, Input, Switch } from 'antd';
import classnames from 'classnames';
import FadeIn from '../FadeIn';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import useForm from '../../hooks/utility/useForm';
import useBusinessProfile from '../../hooks/context/useBusinessProfile';
import AddressFields from '../form/address/AddressFields';

export default function BusinessInfoStep(props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:BusinessInfoStep' });

  const { buttonText, moveForward } = props;
  const { getValue, updating, pushUpdates } = useBusinessProfile();
  const { setErrorFeedback, renderFeedbackLabel } = useFeedbackLabel({ t });

  const { data, update } = useForm({
    display_name: getValue('display_name'),
    legal_name: getValue('legal_name'),
    legal_country: getValue('legal_country'),
    legal_state: getValue('legal_state'),
    legal_city: getValue('legal_city'),
    legal_address: getValue('legal_address'),
    legal_zip_code: getValue('legal_zip_code'),

    has_dedicated_invoice_address: getValue('has_dedicated_invoice_address'),
    invoice_country: getValue('invoice_country'),
    invoice_state: getValue('invoice_state'),
    invoice_city: getValue('invoice_city'),
    invoice_address: getValue('invoice_address'),
    invoice_zip_code: getValue('invoice_zip_code'),
  });

  const save = () =>
    pushUpdates({
      data,
      onSuccess: () => moveForward && moveForward(),
      onError: ({ data }) => setErrorFeedback(data),
    });

  return (
    <FadeIn>
      {renderFeedbackLabel}
      <Form layout="vertical">
        <Form.Item label={t('form.display_name.label')} required>
          <Input
            placeholder={t('form.display_name.placeholder')}
            value={data.display_name ?? ''}
            onChange={(e) => update('display_name', e.target.value)}
          />
        </Form.Item>
        <Form.Item label={t('form.legal_name.label')} tooltip={t('form.legal_name.tooltip')} required>
          <Input
            placeholder={t('form.legal_name.placeholder')}
            value={data.legal_name ?? ''}
            onChange={(e) => update('legal_name', e.target.value)}
          />
        </Form.Item>
        <hr />
        <Flex justify="space-between" gap={16}>
          <h6 className="my-auto">{t('sections.address')}</h6>
          <Flex>
            <label htmlFor="has_dedicated_invoice_address" className="mr-md my-auto">
              {t('form.has_dedicated_invoice_address.label')}
            </label>
            <Switch
              checked={data.has_dedicated_invoice_address}
              onChange={(checked) => update('has_dedicated_invoice_address', checked)}
              className="my-auto"
              id="has_dedicated_invoice_address"
            />
          </Flex>
        </Flex>
        <div className="grid grid-cols-2 md:gap-10 relative">
          <div className={classnames({ 'col-span-2 mt-md': !data.has_dedicated_invoice_address })}>
            <AddressFields
              title={data.has_dedicated_invoice_address ? t('sections.legal_address') : null}
              prefix="legal"
              data={data}
              onChange={update}
              useGrid={!data.has_dedicated_invoice_address}
            />
          </div>
          {data.has_dedicated_invoice_address && (
            <>
              <div className="md:block hidden absolute h-[90%] border-[1px] border-r-none border-[#0E0E0E33] border-solid top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              <AddressFields
                title={t('sections.invoice_address')}
                prefix="invoice"
                data={data}
                onChange={update}
                useGrid={false}
              />
            </>
          )}
        </div>
      </Form>
      <Flex justify="end">
        <Button type="primary" onClick={save} loading={updating}>
          {buttonText ?? t('buttons.next')}
        </Button>
      </Flex>
    </FadeIn>
  );
}
